import { z } from 'zod';
const MarketingChannelSchema = z.string().min(1, "Channel is required");
const MarketingTacticSchema = z.string().min(1, "Tactic is required");
const CurrencyCodeSchema = z.string().min(3, "Currency code is required").max(3, "Currency code must be 3 characters");
const SpendEntrySchema = z.object({
    channelTacticId: z.string().min(1, "Channel/Tactic ID is required"),
    channel: MarketingChannelSchema,
    tactic: MarketingTacticSchema,
    spend: z.number().min(0, "Spend amount cannot be negative"),
    currency: CurrencyCodeSchema,
    notes: z.string().optional(),
}).refine((data) => {
    return data.spend <= 10000000;
}, {
    message: "Spend amount seems unreasonably high (over $10M)",
    path: ["spend"],
});
/**
 * Base spend data schema (without refinements). Used where .partial() is needed
 * since Zod v4 does not support .partial() on refined schemas.
 */
export const SpendDataBaseSchema = z.object({
    entries: z.array(SpendEntrySchema),
    totalSpend: z.number().min(0, "Total spend cannot be negative"),
    primaryCurrency: CurrencyCodeSchema,
    lastUpdated: z.date(),
});
/**
 * Schema for validating complete spend data
 */
export const SpendDataSchema = SpendDataBaseSchema.refine((data) => {
    const channelTacticIds = data.entries.map(entry => entry.channelTacticId);
    return new Set(channelTacticIds).size === channelTacticIds.length;
}, {
    message: "Cannot have duplicate channel/tactic combinations",
    path: ["entries"],
}).refine((data) => {
    const calculatedTotal = data.entries.reduce((sum, entry) => sum + entry.spend, 0);
    return Math.abs(data.totalSpend - calculatedTotal) < 0.01;
}, {
    message: "Total spend must match the sum of all entry amounts",
    path: ["totalSpend"],
});
export const CreateChannelOptionSchema = z.object({
    value: z.string().min(1, "Channel value is required"),
    label: z.string().min(1, "Channel label is required"),
    isActive: z.boolean().default(true),
    sortOrder: z.number().min(0, "Sort order cannot be negative"),
});
export const UpdateChannelOptionSchema = z.object({
    label: z.string().min(1, "Channel label is required").optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().min(0, "Sort order cannot be negative").optional(),
});
export const CreateTacticOptionSchema = z.object({
    value: z.string().min(1, "Tactic value is required"),
    label: z.string().min(1, "Tactic label is required"),
    isActive: z.boolean().default(true),
    sortOrder: z.number().min(0, "Sort order cannot be negative"),
});
export const UpdateTacticOptionSchema = z.object({
    label: z.string().min(1, "Tactic label is required").optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().min(0, "Sort order cannot be negative").optional(),
});
export const CreateCurrencyOptionSchema = z.object({
    code: z.string().min(3, "Currency code is required").max(3, "Currency code must be 3 characters"),
    name: z.string().min(1, "Currency name is required"),
    symbol: z.string().min(1, "Currency symbol is required"),
    isActive: z.boolean().default(true),
    sortOrder: z.number().min(0, "Sort order cannot be negative"),
});
export const UpdateCurrencyOptionSchema = z.object({
    name: z.string().min(1, "Currency name is required").optional(),
    symbol: z.string().min(1, "Currency symbol is required").optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().min(0, "Sort order cannot be negative").optional(),
});
export const CreateChannelTacticConfigSchema = z.object({
    id: z.string().min(1, "Configuration ID is required").regex(/^[a-z0-9-]+$/, "Configuration ID must contain only lowercase letters, numbers, and hyphens"),
    channel: MarketingChannelSchema,
    tactic: MarketingTacticSchema,
    displayName: z.string().min(1, "Display name is required"),
    isActive: z.boolean().default(true),
    sortOrder: z.number().min(1, "Sort order must be at least 1"),
    description: z.string().optional(),
});
export const UpdateChannelTacticConfigSchema = z.object({
    channel: MarketingChannelSchema.optional(),
    tactic: MarketingTacticSchema.optional(),
    displayName: z.string().min(1, "Display name is required").optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().min(1, "Sort order must be at least 1").optional(),
    description: z.string().optional(),
});
